import type { ShipmentStatus } from '@freightapi/core/schemas/shipment'
import { Button, EmptyState, Field, formatDate } from '@freightapi/ui'
import { createFileRoute, Link } from '@tanstack/react-router'
import { type FormEvent, useCallback, useState } from 'react'
import { ResourceView } from '../components/ResourceView'
import {
  type ConsoleShipment,
  fetchShipments,
  PROVIDER_NAMES,
  type ProviderName,
  SHIPMENT_STATUSES,
  type ShipmentFilters,
  type ShipmentPage,
} from '../lib/api'
import { useConsoleI18n } from '../lib/locale'
import { useMode } from '../lib/mode'
import { useResource } from '../lib/resource'

/**
 * The shipments table (M3-04). It reads `/v1/console/shipments`, the session alias of the public
 * list, so the filters, the page size and the cursor are the ones documented for the API — a
 * developer who learns this screen has learned the endpoint.
 *
 * Paging is by cursor, not by page number, because that is what the API offers: the trail of
 * cursors below is the console remembering where it came from, which is the only way "previous"
 * exists on an opaque cursor. And everything is per mode: the switch in the header decides whether
 * this is the sandbox or the real world, and a shipment of the other half is simply not in the list.
 */
export const Route = createFileRoute('/_app/shipments/')({ component: Shipments })

/** The filter form as the inputs hold it: empty string is "no filter", dates are `yyyy-mm-dd`. */
interface FilterDraft {
  readonly status: ShipmentStatus | ''
  readonly reference: string
  readonly provider: ProviderName | ''
  readonly created_after: string
  readonly created_before: string
}

const NO_FILTERS: FilterDraft = {
  status: '',
  reference: '',
  provider: '',
  created_after: '',
  created_before: '',
}

function Shipments() {
  const { t, locale, sign } = useConsoleI18n()
  const { mode } = useMode()
  const [draft, setDraft] = useState<FilterDraft>(NO_FILTERS)
  const [applied, setApplied] = useState<FilterDraft>(NO_FILTERS)
  // Every cursor that was followed to get here; the last one is the page on screen.
  const [trail, setTrail] = useState<readonly string[]>([])
  const [trailMode, setTrailMode] = useState(mode)
  const cursor = trail.at(-1)

  // The other mode is another set of shipments, so a cursor from this one means nothing there.
  // Adjusted while rendering rather than in an effect: this is derived state, and a render that
  // first paints page four of the other mode and then corrects itself would be a lie on screen.
  if (trailMode !== mode) {
    setTrailMode(mode)
    setTrail([])
  }

  const load = useCallback(
    (signal: AbortSignal) =>
      fetchShipments(
        mode,
        { ...toFilters(applied), ...(cursor === undefined ? {} : { cursor }) },
        signal,
      ),
    [mode, applied, cursor],
  )
  const { state } = useResource<ShipmentPage>(load)

  function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    // A new filter is a new result set: the cursors of the old one point into nothing.
    setTrail([])
    setApplied(draft)
  }

  return (
    <section className="flex flex-col gap-6">
      <h1 className="font-semibold text-display text-ink">{t('nav.shipments.label')}</h1>
      <p className="text-body text-muted">{t('shipments.intro')}</p>

      <form
        className="flex flex-wrap items-end gap-4 rounded-card border border-line p-4"
        onSubmit={onSubmit}
      >
        <Field label={t('shipments.filter.status')}>
          {({ id }) => (
            <select
              id={id}
              value={draft.status}
              onChange={(event) =>
                setDraft({ ...draft, status: event.target.value as ShipmentStatus | '' })
              }
              className="min-h-touch rounded-card border border-line bg-surface px-2 text-body text-ink"
            >
              <option value="">{t('shipments.filter.status.any')}</option>
              {SHIPMENT_STATUSES.map((status) => (
                <option key={status} value={status}>
                  {sign(`shipment.status.${status}`)}
                </option>
              ))}
            </select>
          )}
        </Field>
        <Field label={t('shipments.filter.reference')}>
          {({ id }) => (
            <input
              id={id}
              type="text"
              maxLength={100}
              value={draft.reference}
              onChange={(event) => setDraft({ ...draft, reference: event.target.value })}
              className="min-h-touch rounded-card border border-line bg-surface px-3 text-body text-ink"
            />
          )}
        </Field>
        <Field label={t('shipments.filter.provider')}>
          {({ id }) => (
            <select
              id={id}
              value={draft.provider}
              onChange={(event) =>
                setDraft({ ...draft, provider: event.target.value as ProviderName | '' })
              }
              className="min-h-touch rounded-card border border-line bg-surface px-2 text-body text-ink"
            >
              <option value="">{t('shipments.filter.provider.any')}</option>
              {PROVIDER_NAMES.map((provider) => (
                <option key={provider} value={provider}>
                  {provider}
                </option>
              ))}
            </select>
          )}
        </Field>
        <Field label={t('shipments.filter.created_after')}>
          {({ id }) => (
            <input
              id={id}
              type="date"
              value={draft.created_after}
              onChange={(event) => setDraft({ ...draft, created_after: event.target.value })}
              className="min-h-touch rounded-card border border-line bg-surface px-3 text-body text-ink"
            />
          )}
        </Field>
        <Field label={t('shipments.filter.created_before')}>
          {({ id }) => (
            <input
              id={id}
              type="date"
              value={draft.created_before}
              onChange={(event) => setDraft({ ...draft, created_before: event.target.value })}
              className="min-h-touch rounded-card border border-line bg-surface px-3 text-body text-ink"
            />
          )}
        </Field>
        <div className="flex gap-2">
          <Button type="submit">{t('shipments.filter.apply')}</Button>
          <Button
            type="button"
            variant="ghost"
            onClick={() => {
              setDraft(NO_FILTERS)
              setApplied(NO_FILTERS)
              setTrail([])
            }}
          >
            {t('shipments.filter.reset')}
          </Button>
        </div>
      </form>

      <ResourceView state={state}>
        {(page) =>
          page.data.length === 0 ? (
            <EmptyState
              title={t('shipments.empty.title')}
              description={t('shipments.empty.body')}
            />
          ) : (
            <>
              <table className="w-full border-collapse text-left text-body">
                <thead>
                  <tr className="border-line border-b text-caption text-muted">
                    <th scope="col" className="py-2 pr-4 font-medium">
                      {t('shipments.column.reference')}
                    </th>
                    <th scope="col" className="py-2 pr-4 font-medium">
                      {t('shipments.column.status')}
                    </th>
                    <th scope="col" className="py-2 pr-4 font-medium">
                      {t('shipments.column.route')}
                    </th>
                    <th scope="col" className="py-2 pr-4 font-medium">
                      {t('shipments.column.provider')}
                    </th>
                    <th scope="col" className="py-2 font-medium">
                      {t('shipments.column.created')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {page.data.map((shipment) => (
                    <tr key={shipment.id} className="border-line border-b align-top">
                      <td className="py-3 pr-4">
                        <Link
                          to="/shipments/$id"
                          params={{ id: shipment.id }}
                          className="font-medium text-brand-700 underline"
                        >
                          {shipment.reference ?? shipment.id}
                        </Link>
                        <span className="block font-mono text-caption text-muted">
                          {shipment.id}
                        </span>
                      </td>
                      <td className="py-3 pr-4">
                        <span className="block text-ink">
                          {sign(`shipment.status.${shipment.status}`)}
                        </span>
                        <span className="block text-caption text-muted">
                          {t('shipments.version', { version: shipment.version })}
                        </span>
                      </td>
                      <td className="py-3 pr-4 text-caption text-muted">{routeOf(shipment)}</td>
                      <td className="py-3 pr-4 text-caption text-muted">{shipment.provider}</td>
                      <td className="py-3 text-caption text-muted">
                        {formatDate(shipment.created_at, locale)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <div className="flex gap-2">
                <Button
                  variant="ghost"
                  disabled={trail.length === 0}
                  onClick={() => setTrail((followed) => followed.slice(0, -1))}
                >
                  {t('shipments.page.previous')}
                </Button>
                <Button
                  variant="ghost"
                  disabled={page.next_cursor === null}
                  onClick={() =>
                    setTrail((followed) =>
                      page.next_cursor === null ? followed : [...followed, page.next_cursor],
                    )
                  }
                >
                  {t('shipments.page.next')}
                </Button>
              </div>
            </>
          )
        }
      </ResourceView>
    </section>
  )
}

/** The form as the API reads it: a day in a date input is the whole day in the query. */
function toFilters(draft: FilterDraft): ShipmentFilters {
  return {
    ...(draft.status === '' ? {} : { status: draft.status }),
    ...(draft.reference.trim() === '' ? {} : { reference: draft.reference.trim() }),
    ...(draft.provider === '' ? {} : { provider: draft.provider }),
    ...(draft.created_after === ''
      ? {}
      : { created_after: `${draft.created_after}T00:00:00.000Z` }),
    ...(draft.created_before === ''
      ? {}
      : { created_before: `${draft.created_before}T23:59:59.999Z` }),
  }
}

/** Where it goes, in the two words a dispatcher recognises a shipment by. */
function routeOf(shipment: ConsoleShipment): string {
  const from = shipment.pickup.address
  const to = shipment.delivery.address
  return `${from.city} (${from.country}) → ${to.city} (${to.country})`
}
